import React, { useState } from "react";
import { ItemInput } from "../../../@types";
// @ts-ignore
import AddIcon from "../../../assets/icons/AddIcon";
import { Button } from "../../common/Button";

interface Item {
  id: string;
  name: string;
  quantity: number;
  rate: number;
  amount: number;
}

interface AddItemProps {
  onSave: (savedItems: ItemInput[]) => void;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  editItem?: ItemInput; // Ensure editItem is optional and matches the correct type
  setEditItem: (item: ItemInput | undefined) => void;
  isEdit: boolean;
  deleteOldData: () => void;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddItem: React.FC<AddItemProps> = ({
  onSave,
  setIsOpen,
  editItem,
  isEdit,
  deleteOldData,
  setIsEdit,
}) => {
  const [items, setItems] = useState<ItemInput[]>(
    editItem
      ? [
          {
            ...editItem,
            quantity: editItem.quantity ?? 0,
            rate: editItem.rate ?? 0,
            amount: (editItem.quantity ?? 0) * (editItem.rate ?? 0),
          },
        ]
      : [
          {
            id: Date.now().toString(),
            name: "",
            quantity: undefined,
            rate: undefined,
          },
        ],
  );
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  // Handle input changes for a specific item
  const handleUpdate = (
    index: number,
    field: keyof ItemInput,
    value: string | number,
  ) => {
    setItems((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              [field]:
                field === "quantity" || field === "rate"
                  ? Number(value)
                  : value,
            }
          : item,
      ),
    );
  };

  // Add new item line
  const handleAddNewLine = () => {
    setItems((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        name: "",
        quantity: undefined,
        rate: undefined,
      },
    ]);
  };

  // Remove a specific item
  const handleRemoveLine = (id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  // Save Items
  const handleSaveItems = () => {
    const newErrors: Record<string, string[]> = {};
    const validatedItems = items.map((item, index) => {
      const itemErrors: string[] = [];
      if (!item.name) itemErrors.push("name");
      if (!item.quantity || item.quantity <= 0) itemErrors.push("quantity");
      if (!item.rate || item.rate <= 0) itemErrors.push("rate");

      if (itemErrors.length > 0) newErrors[index] = itemErrors;

      return { ...item, amount: (item.quantity ?? 0) * (item.rate ?? 0) };
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(validatedItems);
    setIsEdit(false);
    setIsOpen(false);
  };

  return (
    // Tailwind Modal Wrapper
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white shadow-lg w-full max-w-lg h-full flex flex-col p-6">
        {/* Header */}
        <div className="mt-[1em]">
          <div className="flex items-start justify-start">
            <button onClick={() => setIsOpen(false)}>&larr; Back</button>
          </div>
          <div className="flex justify-center items-center mb-5 w-full mt-[-1.8em]">
            <p className="font-semibold text-[20px] text-center flex items-center justify-center">
              {isEdit ? "Edit Item" : "Add Item"}
            </p>
          </div>
        </div>

        {/* Scrollable Form Area */}
        <div className="flex-1 overflow-y-auto">
          <form className="space-y-4">
            {items.map((item, index) => (
              <div key={item.id} className="p-4 border rounded-md bg-[#FCFCFD]">
                <input
                  type="text"
                  placeholder="Item name"
                  className={`w-full px-3 py-2 border placeholder:text-[#98A2B3] ${
                    errors[index]?.includes("name")
                      ? "border-red-500"
                      : "border-gray-300"
                  } rounded-md`}
                  value={item.name}
                  onChange={(e) => handleUpdate(index, "name", e.target.value)}
                />
                <div className="flex flex-col gap-4 mt-3">
                  <input
                    type="number"
                    placeholder="Enter quantity"
                    className={`w-full px-3 py-2 border placeholder:text-[#98A2B3] ${
                      errors[index]?.includes("quantity")
                        ? "border-red-500"
                        : "border-gray-300"
                    } rounded-md`}
                    value={
                      item.quantity !== undefined && item.quantity !== 0
                        ? item.quantity
                        : ""
                    }
                    onChange={(e) =>
                      handleUpdate(index, "quantity", e.target.value)
                    }
                  />
                  <input
                    type="number"
                    placeholder="Enter rate"
                    className={`w-full px-3 py-2 border placeholder:text-[#98A2B3] ${
                      errors[index]?.includes("rate")
                        ? "border-red-500"
                        : "border-gray-300"
                    } rounded-md`}
                    value={
                      item.rate !== undefined && item.rate !== 0
                        ? item.rate
                        : ""
                    }
                    onChange={(e) =>
                      handleUpdate(index, "rate", e.target.value)
                    }
                  />
                </div>
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveLine(item.id)}
                    className="mt-2 text-red-500"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </form>

          {/* Add New Line Button */}
          <div
            onClick={handleAddNewLine}
            className="flex items-center font-semibold justify-end text-[#044E97] cursor-pointer mt-4"
          >
            <span className="mr-2">
              <AddIcon />
            </span>{" "}
            Add new line
          </div>
        </div>

        {/* Sticky Bottom Action Buttons */}
        <div className="sticky bottom-0 left-0 w-full bg-white py-4 flex justify-center gap-x-5 ">
          <Button
            outline
            variant="secondary"
            text="Cancel"
            onClick={() => setIsOpen(false)}
            size="small"
          />
        
          <Button
            text="Add item"
            variant="secondary"
            onClick={handleSaveItems}
            size="small"
          />
        </div>
      </div>
    </div>
  );
};

export default AddItem;
